import { useCallback, useMemo, useState } from "react";

import { FormattedMessage, useIntl } from "react-intl";

import { transformChunks } from "shared/components/TextWrapper";
import { IntlMessageId } from "shared/lang";
import { Button } from "ui/Button/Button";
import { Form } from "ui/Form/Form";
import { Input } from "ui/Input/Input";
import { Modal } from "ui/Modal/Modal";
import { BodyM, HeaderS } from "ui/Typography/Typography";

type ModalLabels = {
  titleLabel: IntlMessageId;
  bodyLabel: IntlMessageId;
  confirmLabel: IntlMessageId;
  cancelLabel: IntlMessageId;
};
interface StockChangeConfirmationModalProps {
  isOpen: boolean;
  currentValue: number;
  isInbound?: boolean;
  isStockCheck?: boolean;
  onClickYes: () => void;
  onClickNo: () => void;
}

function StockChangeConfirmationModal({
  isOpen,
  currentValue,
  isInbound = false,
  isStockCheck = false,
  onClickYes,
  onClickNo,
}: StockChangeConfirmationModalProps) {
  const intl = useIntl();
  const count = Math.abs(currentValue);
  const [enteredValue, setEnteredValue] = useState("");
  const [inputError, setInputError] = useState(false);

  const handleCloseModal = useCallback(() => {
    setEnteredValue("");
    setInputError(false);
    onClickNo();
  }, [onClickNo]);

  const isValueValid = useCallback((value: string, expected: number) => {
    return parseFloat(value) === expected;
  }, []);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValueValid(enteredValue, count)) {
      setInputError(true);
      return;
    }

    setEnteredValue("");
    setInputError(false);
    onClickYes();
  };

  const labels: ModalLabels = useMemo(() => {
    if (isInbound) {
      return {
        titleLabel: "components.stock-change-confirmation-modal.inbound.title",
        bodyLabel: "components.stock-change-confirmation-modal.inbound.body",
        confirmLabel: "components.stock-change-confirmation-modal.confirm",
        cancelLabel: "generic.actions.cancel",
      };
    }

    if (isStockCheck) {
      return {
        titleLabel: "components.stock-change-confirmation-modal.stock-check.title",
        bodyLabel: "components.stock-change-confirmation-modal.stock-check.body",
        confirmLabel: "components.stock-change-confirmation-modal.confirm",
        cancelLabel: "generic.actions.cancel",
      };
    }

    return {
      titleLabel: "components.stock-change-confirmation-modal.correction.title",
      bodyLabel: "components.stock-change-confirmation-modal.correction.body",
      confirmLabel:
        currentValue > 0
          ? "components.stock-change-confirmation-modal.add"
          : "components.stock-change-confirmation-modal.remove",
      cancelLabel: "generic.actions.back",
    };
  }, [isInbound, isStockCheck, currentValue]);

  const { titleLabel, bodyLabel, confirmLabel, cancelLabel } = labels;

  return (
    <Modal
      trapFocus={false}
      closeOnOverlayClick={false}
      isCentered
      isOpen={isOpen}
      onClose={handleCloseModal}
    >
      <Modal.Content marginX="s300">
        <Form w="100%" onSubmit={handleFormSubmit}>
          <Modal.Header textAlign="center" pb="s100">
            <HeaderS>
              <FormattedMessage id={titleLabel} />
            </HeaderS>
          </Modal.Header>
          <Modal.Body textAlign="center">
            <BodyM color="grey.700" sx={{ whiteSpace: "pre-wrap" }}>
              <FormattedMessage
                id={bodyLabel}
                values={{
                  count,
                  ...transformChunks({ b: { as: "strong" } }),
                }}
              />
            </BodyM>

            <Form.Field mt="s300" isInvalid={inputError}>
              <Input
                h="48px"
                type="text"
                inputMode="numeric"
                pattern="\d*"
                value={enteredValue}
                onChange={(e) => {
                  setEnteredValue(e.target.value);
                  setInputError(false);
                }}
                placeholder={intl.formatMessage({
                  id: "components.stock-change-confirmation-modal.placeholder",
                })}
                autoComplete="off"
              />
              {inputError && (
                <Form.ErrorMessage>
                  <FormattedMessage id="components.stock-change-confirmation-modal.error" />
                </Form.ErrorMessage>
              )}
            </Form.Field>
          </Modal.Body>

          <Modal.Footer flexDir="column">
            <Button
              type="submit"
              isFullWidth
              variant="primary"
              size="lg"
              data-testid="yes-no-modal-yes-button"
              disabled={!enteredValue || inputError}
            >
              <FormattedMessage id={confirmLabel} values={{ count }} />
            </Button>
            <Button
              mt="s100"
              onClick={handleCloseModal}
              isFullWidth
              variant="secondary"
              size="lg"
              data-testid="yes-no-modal-no-button"
            >
              <FormattedMessage id={cancelLabel} />
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Content>
    </Modal>
  );
}

export default StockChangeConfirmationModal;
